import { useCallback } from 'react';

import type { ExperimentConfig, ResolvedExperimentData } from '@change-corgi/experiments/configs';

import { useCallOnce } from './useCallOnce';
import { useTrackOnce } from './useTrackOnce';

type Options = Readonly<{
	/**
	 * Reset keys that re-fire the event when changed. Required when the same component instance
	 * is reused across logical contexts (e.g. SPA petition change with no remount), since
	 * variations are deterministic per user and would otherwise dedup the legitimate fire.
	 */
	resetKeys?: readonly unknown[];
}>;

/**
 * Returns a function that fires `$exposure` (`flag_key`/`variant`/`variant_name`) for the given
 * experiment. Deduped per mount via `useTrackOnce`. Treated control users fire too (control is a
 * treatment arm for lift analysis).
 *
 * No-ops (never sends `$exposure`) when:
 * - the experiment is `experiment-service` and `treated === false` — deferred treatment (the GQL
 *   field's `treat: false`), not yet a real assignment;
 * - `experiment.fallback` is set — the value isn't a real assignment (kill-switch, disabled,
 *   not-found, sdk-error, bot, …), so exposure would misattribute it;
 * - `experiment.override` is set — a QA/dev override forced the variation, so exposure would
 *   misattribute it too. Unlike the other no-op cases, this logs the experiment name and variant to
 *   the console (once per mount, same dedup as the real event), so QA/dev can confirm exposure
 *   would have fired if they weren't overriding; or
 * - `experimentName` is empty — e.g. a `growthbook` experiment that matched a rule with no distinct
 *   GrowthBook tracking key (a real assignment, just nothing to attribute `$exposure` to).
 *
 * @example
 * const trackExperimentExposure = useTrackExperimentExposure(experiment);
 * useEffect(() => { void trackExperimentExposure(); }, [trackExperimentExposure]);
 */
export function useTrackExperimentExposure<E extends ExperimentConfig>(
	experiment: ResolvedExperimentData<E>,
	{ resetKeys = [] }: Options = {},
): (extraProperties?: Readonly<Record<string, string | number | boolean>>) => Promise<boolean> {
	const { treated, type, fallback, override } = experiment;
	// `ResolvedExperimentData<E>`'s `experimentName`/`variation` are typed via generic params
	// (`EXP_NAME`/`VAR extends string`) that TS can't resolve through this function's own generic `E`
	// — the conditional type collapses them to `any` inside a generic body even though every concrete
	// instantiation guarantees a `string`. Narrow explicitly here, once, instead of letting `any` leak
	// into every downstream usage below.
	const experimentName = experiment.experimentName as string;
	const variation = experiment.variation as string;

	// `variant_name` is the human-readable variation. `variant_key`/`variant` is what Amplitude
	// Experiment keys assignment on: for growthbook it's the GrowthBook variation key
	// (`experimentResult.key`); other types have no distinct key, so it mirrors the variation.
	//
	// For growthbook we intentionally do NOT fall back to `variation` here: an *unassigned* growthbook
	// experiment has an empty `variantKey` (and empty `experimentName`), and the `if (!experimentName)`
	// guard below drops the event entirely — so an empty key is never emitted. Falling back to
	// `variation` would silently send the display name as the key if that guard ever changed.
	const variantKey = type === 'growthbook' ? experiment.variantKey : variation;

	// A growthbook override forces the feature value directly, bypassing rule matching, so
	// `experimentName` (which only comes from a matched rule) is empty — use `featureKey` so the
	// override log below still identifies which experiment it's for.
	const overrideExperimentName = type === 'growthbook' ? experiment.featureKey : experimentName;

	const exposureDeps = [experimentName, variation, variantKey, ...resetKeys];
	const trackExposureOnce = useTrackOnce(exposureDeps);
	// Same "once per mount, reset by exposureDeps" semantics as trackExposureOnce, so the log fires
	// exactly when a real exposure would have — not on every render/call.
	const logOverrideOnce = useCallOnce(logOverrideExposure, exposureDeps);

	return useCallback(
		async (extraProperties?: Readonly<Record<string, string | number | boolean>>): Promise<boolean> => {
			if (type === 'experiment-service' && !treated) return false;
			if (fallback) return false;

			if (override) {
				// exposure was never actually sent, regardless of whether this call was the one that logged
				await logOverrideOnce(overrideExperimentName, variation);
				return false;
			}

			if (!experimentName) return false;

			// Caller-supplied properties ride along for funnel segmentation (e.g. the constituent experiment's
			// identification method + "saw DM name/threshold" flags), but can never shadow the core keys.
			return trackExposureOnce('$exposure', {
				...extraProperties,
				flag_key: experimentName,
				variant: variantKey,
				variant_name: variation,
				experiment_type: type,
			});
		},
		[
			trackExposureOnce,
			logOverrideOnce,
			treated,
			type,
			fallback,
			override,
			experimentName,
			variation,
			variantKey,
			overrideExperimentName,
		],
	);
}

async function logOverrideExposure(experimentName: string, variant: string): Promise<boolean> {
	// oxlint-disable-next-line no-console
	console.log(
		`%c$exposure SUPPRESSED by override:%c ${experimentName} (variant: ${variant})`,
		'color: orange',
		'color: default',
	);
	return true;
}
